import { useState } from 'react';
import { Panel } from '../ui/Panel';
import { Field } from '../ui/Field';
import { Toggle } from '../ui/Toggle';
import { NumberInput } from '../ui/NumberInput';
import { inputCls } from '../ui/styles';
import { undoToast } from '../ui/undoToast';
import { activePromo } from '../../core/rules/promo';
import { euro } from '../../core/format/currency';
import { isIsoDate, today } from '../../core/format/dates';
import type { Product } from '../../core/types/catalog';
type Props = {
  product: Product;
  set: (patch: Partial<Product>) => void;
};
const DEFAULT_LABEL = 'Leveranciersactie';

/**
 * Leveranciersactie aan of uit. Aan = de huidige prijs wordt de doorgestreepte adviesprijs
 * en Sarah vult de actieprijs in. Uit = de adviesprijs komt terug, niets blijft hangen.
 * Alleen voor acties die het merk goedkeurt; een eigen korting mag niet.
 */
export function PromoPanel({
  product,
  set
}: Props) {
  const [pastDate, setPastDate] = useState(false);
  const on = Boolean(product.promo);
  const promo = product.promo;
  const minDate = today();
  const regular = product.regularPrice;
  const status = on ? activePromo(product) : null;
  const expired = on && product.promo?.endsOn && isIsoDate(product.promo.endsOn) && !status;
  const priceError = on && regular !== undefined && !(product.price < regular);
  const toggle = (next: boolean) => {
    if (next) {
      set({
        promo: {
          label: DEFAULT_LABEL,
          endsOn: ''
        },
        regularPrice: product.price
      });
      return;
    }
    // Uitzetten wist actieprijs, tekst en einddatum. Eén verkeerde tik mag dat niet kosten:
    // de actie staat een paar seconden terug te zetten, precies zoals hij was.
    const before = {
      promo: product.promo,
      regularPrice: product.regularPrice,
      price: product.price
    };
    set({
      promo: undefined,
      regularPrice: undefined,
      price: regular ?? product.price
    });
    undoToast('Leveranciersactie uitgezet', () => set(before), {
      description: regular !== undefined ? `De adviesprijs van ${euro(regular, {
        cents: true
      })} staat weer op de site.` : undefined,
      restored: 'Actie staat weer aan'
    });
  };
  return <Panel title="Leveranciersactie" data-id="element-826" data-mp="3kj6p5zvk9i7m">
      <Toggle checked={on} onChange={toggle} label="Er loopt een actie van het merk" description="Alleen bij een actie die het merk goedkeurt. De adviesprijs wordt doorgestreept." data-id="element-827" data-mp="3ki5dbt6q0rou" />
      {on && promo && <div className="mt-4 space-y-4" data-id="element-828" data-mp="c3wtb9i9l98a">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2" data-id="element-829" data-mp="c2lnz5967tf2">
            <Field label="Adviesprijs (€)" data-id="element-830" data-mp="74k1sbgg7dzd">
              <NumberInput decimals value={regular} onChange={v => set({
            regularPrice: v
          })} emptyMessage="Vul de adviesprijs in." data-id="element-831" data-mp="72quqv1xarqu" />
            </Field>
            <Field label="Actieprijs (€)" data-id="element-832" data-mp="3tctjrmo18ff8">
              <NumberInput decimals value={product.price || undefined} onChange={v => set({
            price: v ?? 0
          })} emptyMessage="Vul de actieprijs in." data-id="element-833" data-mp="3te4iv96j8z00" />
            </Field>
          </div>
          {priceError && <p role="alert" className="text-[12px] text-clay" data-id="element-834" data-mp="2hz7qbpb53rnw">
              De actieprijs moet lager zijn dan de adviesprijs, anders toont de site geen actie.
            </p>}
          <Field label="Tekst op de kaart" hint="Bijv. “Actie van emerginC”." data-id="element-835" data-mp="12bujpdm4ut3u">
            <input value={promo.label} onChange={e => set({
          promo: {
            ...promo,
            label: e.target.value
          }
        })} className={inputCls} maxLength={40} data-id="element-836" data-mp="17bfdzaqcyda8" />
          </Field>
          <Field label="Loopt tot en met" hint="Daarna verdwijnt het actielabel vanzelf. Zet de prijs dan wel terug." data-id="element-837" data-mp="1h3l7wq0674s2">
            <input type="date"
        // De kalender biedt geen dagen in het verleden aan. Een getypte datum wordt hieronder
        // nog eens gecontroleerd, want niet elke browser houdt zich aan `min` bij typen.
        min={minDate} value={isIsoDate(promo.endsOn) ? promo.endsOn : ''} onChange={e => {
          const v = e.target.value;
          // Halverwege typen (nog geen hele datum) of leegmaken mag altijd.
          const past = isIsoDate(v) && v < minDate;
          setPastDate(past);
          if (!past) set({
            promo: {
              ...promo,
              endsOn: v
            }
          });
        }} onBlur={() => setPastDate(false)} aria-invalid={pastDate || undefined} aria-describedby={pastDate ? 'promo-date-error' : undefined} className={inputCls} data-id="element-838" data-mp="1l33qhf4ir3rq" />
            {pastDate && <p id="promo-date-error" role="alert" className="mt-1.5 text-[12px] text-clay" data-id="element-839" data-mp="18ffjt3vvk8tv">
                Kies vandaag of later. Een actie die al voorbij is, zet je gewoon uit.
              </p>}
          </Field>
          <p className={`text-[13px] ${expired ? 'text-clay' : 'text-muted'}`} data-id="element-840" data-mp="1cgstxq602y6l">
            {expired ? 'Deze actie is verlopen. Zet de actie uit, dan komt de adviesprijs terug.' : status && regular !== undefined && !priceError ? `Op de site: ${euro(product.price, {
          cents: true
        })} in plaats van ${euro(regular, {
          cents: true
        })}${status.until ? `, ${status.until}` : ''}.` : 'Vul een tekst in, dan staat de actie op de site.'}
          </p>
        </div>}
    </Panel>;
}